// C:\IntegraNube_WebStore\src\components\BotonContactoFlotante.js
'use strict';

import React, {
  useEffect,
  useMemo,
  useState,
} from 'react';

import {
  Box,
  ButtonBase,
  ClickAwayListener,
  Fab,
  Paper,
  Stack,
  Tooltip,
  Typography,
  Zoom,
} from '@mui/material';

import {
  ChatBubbleOutlineOutlined as ContactIcon,
  Close as CloseIcon,
  EmailOutlined as EmailIcon,
  WhatsApp as WhatsAppIcon,
} from '@mui/icons-material';

import {
  useLocation,
  useNavigate,
} from 'react-router-dom';

import api from '../api/api';

/* ===================================================================== */
/*  Helpers tienda                                                       */
/* ===================================================================== */

function resolveTiendaData(source) {
  if (!source || typeof source !== 'object') {
    return {};
  }

  if (
    source.status &&
    source.data &&
    typeof source.data === 'object'
  ) {
    return source.data;
  }

  if (
    source.data &&
    typeof source.data === 'object' &&
    (
      source.data.contacto ||
      source.data.nombre
    )
  ) {
    return source.data;
  }

  return source;
}

function cleanString(value) {
  const result =
    String(value ?? '').trim();

  return result || null;
}

function safeColor(value, fallback) {
  return cleanString(value) || fallback;
}

/* ===================================================================== */
/*  WhatsApp                                                             */
/* ===================================================================== */

function normalizeWhatsAppNumber(value) {
  const raw =
    cleanString(value);

  if (!raw) {
    return null;
  }

  let digits =
    raw.replace(/\D/g, '');

  if (
    digits.length === 9 &&
    digits.startsWith('9')
  ) {
    digits = `56${digits}`;
  }

  if (
    digits.length < 8 ||
    digits.length > 15
  ) {
    return null;
  }

  return digits;
}

function getCurrentUrl() {
  if (
    typeof window === 'undefined'
  ) {
    return '';
  }

  return window.location.href;
}

function extractProduct(payload) {
  if (
    !payload ||
    typeof payload !== 'object'
  ) {
    return null;
  }

  if (
    payload.data &&
    typeof payload.data === 'object' &&
    !Array.isArray(payload.data)
  ) {
    if (
      payload.data.data &&
      typeof payload.data.data === 'object' &&
      !Array.isArray(payload.data.data)
    ) {
      return payload.data.data;
    }

    return payload.data;
  }

  return payload;
}

function buildGeneralMessage({
  nombreTienda,
  url,
}) {
  const lines = [
    `¡Hola ${nombreTienda}!`,
    '',
    'Estoy visitando su tienda online y necesito realizar una consulta.',
  ];

  if (url) {
    lines.push(
      '',
      `Estoy escribiendo desde: ${url}`
    );
  }

  return lines.join('\n');
}

function buildProductMessage({
  nombreTienda,
  producto,
  url,
}) {
  const nombreProducto =
    cleanString(
      producto?.nombre ||
      producto?.titulo ||
      producto?.name
    ) ||
    'este producto';

  const sku =
    cleanString(
      producto?.sku ||
      producto?.codigo ||
      producto?.codigoBarras
    );

  const lines = [
    `¡Hola ${nombreTienda}!`,
    '',
    sku
      ? `Estoy mirando la ficha del producto "${nombreProducto}" (SKU ${sku}) y tengo una consulta.`
      : `Estoy mirando la ficha del producto "${nombreProducto}" y tengo una consulta.`,
  ];

  if (url) {
    lines.push(
      '',
      `Producto: ${url}`
    );
  }

  return lines.join('\n');
}

/* ===================================================================== */
/*  Resolver producto según la ruta                                      */
/* ===================================================================== */

function getProductIdentifierFromPath(
  pathname
) {
  const match =
    String(pathname || '')
      .match(/^\/producto\/([^/]+)\/?$/i);

  if (!match) {
    return null;
  }

  let identifier = '';

  try {
    identifier =
      decodeURIComponent(
        match[1]
      ).trim();
  } catch (_) {
    return null;
  }

  if (
    !identifier ||
    identifier.includes('.')
  ) {
    return null;
  }

  return identifier;
}

/* ===================================================================== */
/*  Componente                                                           */
/* ===================================================================== */

export default function BotonContactoFlotante({
  tienda = null,
  hidden = false,
}) {
  const location =
    useLocation();

  const navigate =
    useNavigate();

  const data =
    useMemo(
      () =>
        resolveTiendaData(tienda),
      [tienda]
    );

  const nombreTienda =
    cleanString(
      data?.nombre
    ) ||
    'Tienda Online';

  const colorPrimario =
    safeColor(
      data?.branding?.colorPrimario,
      '#1976d2'
    );

  /*
   * WHATSAPP:
   * Debe existir un número válido Y estar expresamente habilitado
   * whatsapp_flotante_habilitado -> funciones.whatsappFlotante.
   */
  const whatsapp =
    normalizeWhatsAppNumber(
      data?.contacto?.whatsapp
    );

  const whatsappHabilitado =
    Boolean(whatsapp) &&
    data?.funciones
      ?.whatsappFlotante === true;

  /*
   * EMAIL / FORMULARIO:
   * No depende simplemente de que exista contacto.email.
   * Debe estar habilitado el nuevo formulario.
   */
  const formularioHabilitado =
    data?.contacto
      ?.formulario
      ?.habilitado === true;

  /*
   * Si no existe ningún canal, el componente desaparece.
   */
  const canalesActivos =
    Number(whatsappHabilitado) +
    Number(formularioHabilitado);

  const ambosHabilitados =
    canalesActivos === 2;

  const [
    menuOpen,
    setMenuOpen,
  ] = useState(false);

  const productIdentifier =
    useMemo(
      () =>
        getProductIdentifierFromPath(
          location.pathname
        ),
      [location.pathname]
    );

  const [
    producto,
    setProducto,
  ] = useState(null);

  /* =================================================================== */
  /*  Cargar producto para conservar contexto de la ficha                */
  /* =================================================================== */

  useEffect(() => {
    let mounted = true;

    const controller =
      new AbortController();

    if (
      !productIdentifier ||
      (
        !whatsappHabilitado &&
        !formularioHabilitado
      )
    ) {
      setProducto(null);

      return () => {
        mounted = false;
        controller.abort();
      };
    }

    async function loadProduct() {
      try {
        const response =
          await api.get(
            `/productos/${encodeURIComponent(
              productIdentifier
            )}`,
            {
              signal:
                controller.signal,
            }
          );

        if (!mounted) {
          return;
        }

        const result =
          extractProduct(
            response?.data
          );

        setProducto(
          result &&
          typeof result === 'object'
            ? result
            : null
        );
      } catch (error) {
        if (
          error?.name !==
            'CanceledError' &&
          error?.name !==
            'AbortError' &&
          error?.code !==
            'ERR_CANCELED'
        ) {
          console.warn(
            '[ContactButton] No fue posible cargar el producto:',
            error?.message || error
          );
        }

        if (mounted) {
          setProducto(null);
        }
      }
    }

    loadProduct();

    return () => {
      mounted = false;
      controller.abort();
    };
  }, [
    productIdentifier,
    whatsappHabilitado,
    formularioHabilitado,
  ]);

  /*
   * Cerrar el menú al navegar.
   */
  useEffect(() => {
    setMenuOpen(false);
  }, [
    location.pathname,
    location.search,
  ]);

  /* =================================================================== */
  /*  Mensaje WhatsApp                                                   */
  /* =================================================================== */

  const whatsappMessage =
    useMemo(() => {
      const url =
        getCurrentUrl();

      if (
        productIdentifier &&
        producto
      ) {
        return buildProductMessage({
          nombreTienda,
          producto,
          url,
        });
      }

      return buildGeneralMessage({
        nombreTienda,
        url,
      });
    }, [
      nombreTienda,
      productIdentifier,
      producto,
      location.pathname,
      location.search,
    ]);

  /*
   * Contexto que se conserva al pasar al formulario.
   *
   * Para producto enviamos el ID real que Store obtuvo desde su endpoint.
   * Nombre/SKU son sólo informativos para React; API vuelve a consultar
   * el producto por ID antes de guardar la consulta.
   *
   * Para cualquier otra sección conservamos la URL exacta de origen.
   */
  const contextoContacto =
    useMemo(() => {
      const paginaOrigen =
        getCurrentUrl();

      const productoId =
        Number(
          producto?.id
        );

      if (
        productIdentifier &&
        Number.isSafeInteger(
          productoId
        ) &&
        productoId > 0
      ) {
        return {
          tipo:
            'producto',

          paginaOrigen,

          productoId,

          productoNombre:
            cleanString(
              producto?.nombre ||
              producto?.titulo ||
              producto?.name
            ),

          productoSku:
            cleanString(
              producto?.sku ||
              producto?.codigo ||
              producto?.codigoBarras
            ),

          productoSlug:
            cleanString(
              producto?.slug
            ) ||
            productIdentifier,
        };
      }

      return {
        tipo:
          'general',

        paginaOrigen,

        tituloPagina:
          typeof document !==
            'undefined'
            ? cleanString(
                document.title
              )
            : null,
      };
    }, [
      productIdentifier,
      producto,
      location.pathname,
      location.search,
    ]);

  const openWhatsapp = () => {
    if (
      !whatsappHabilitado ||
      !whatsapp
    ) {
      return;
    }

    setMenuOpen(false);

    const whatsappUrl =
      `https://wa.me/${whatsapp}` +
      `?text=${encodeURIComponent(
        whatsappMessage
      )}`;

    const newWindow =
      window.open(
        whatsappUrl,
        '_blank',
        'noopener,noreferrer'
      );

    if (newWindow) {
      newWindow.opener = null;
    }
  };

  const openFormulario = () => {
    if (!formularioHabilitado) {
      return;
    }

    setMenuOpen(false);

    navigate(
      '/contacto',
      {
        state: {
          contactoContexto:
            contextoContacto,
        },
      }
    );
  };

  if (
    hidden ||
    canalesActivos === 0
  ) {
    return null;
  }

  /*
   * Si sólo existe un canal, el botón actúa directamente.
   * Si existen ambos, abre/cierra el selector.
   */
  const handleMainClick = () => {
    if (ambosHabilitados) {
      setMenuOpen(
        (value) => !value
      );
      return;
    }

    if (whatsappHabilitado) {
      openWhatsapp();
      return;
    }

    openFormulario();
  };

  const mainTooltip =
    ambosHabilitados
      ? (
          menuOpen
            ? 'Cerrar opciones de contacto'
            : 'Contactar'
        )
      : whatsappHabilitado
        ? (
            productIdentifier
              ? 'Consultar por este producto'
              : 'Consultar por WhatsApp'
          )
        : (
            productIdentifier
              ? 'Consultar por este producto'
              : 'Enviar una consulta'
          );

  const mainAriaLabel =
    ambosHabilitados
      ? 'Opciones de contacto'
      : whatsappHabilitado
        ? 'Contactar por WhatsApp'
        : 'Enviar consulta por correo';

  const mainBg =
    ambosHabilitados ||
    formularioHabilitado
      ? colorPrimario
      : '#25D366';

  const mainIcon =
    menuOpen &&
    ambosHabilitados
      ? (
          <CloseIcon
            sx={{
              fontSize: {
                xs: 28,
                sm: 30,
              },
            }}
          />
        )
      : ambosHabilitados
        ? (
            <ContactIcon
              sx={{
                fontSize: {
                  xs: 29,
                  sm: 32,
                },
              }}
            />
          )
        : whatsappHabilitado
          ? (
              <WhatsAppIcon
                sx={{
                  fontSize: {
                    xs: 30,
                    sm: 33,
                  },
                }}
              />
            )
          : (
              <EmailIcon
                sx={{
                  fontSize: {
                    xs: 29,
                    sm: 32,
                  },
                }}
              />
            );

  return (
    <ClickAwayListener
      onClickAway={() =>
        setMenuOpen(false)
      }
    >
      <Box
        sx={{
          position: 'fixed',

          right: {
            xs: 18,
            sm: 24,
          },

          bottom: {
            xs: 18,
            sm: 24,
          },

          zIndex: 1250,
        }}
      >
        {/* ============================================================= */}
        {/* Selector cuando existen ambos canales                         */}
        {/* ============================================================= */}

        {ambosHabilitados ? (
          <Zoom in={menuOpen}>
            <Paper
              elevation={0}
              sx={{
                position:
                  'absolute',

                right: 0,
                bottom: {
                  xs: 68,
                  sm: 74,
                },

                width: {
                  xs: 250,
                  sm: 270,
                },

                p: 1,
                border:
                  '1px solid #e5e7eb',
                borderRadius: 3,
                bgcolor: '#fff',

                boxShadow:
                  '0 16px 40px rgba(15, 23, 42, 0.18)',

                transformOrigin:
                  'bottom right',
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  display: 'block',
                  px: 1.25,
                  pt: 0.75,
                  pb: 0.5,
                  color: '#64748b',
                  fontWeight: 800,
                }}
              >
                ¿Cómo desea contactarnos?
              </Typography>

              <Stack spacing={0.5}>
                <ContactOption
                  icon={
                    <WhatsAppIcon />
                  }
                  title="WhatsApp"
                  subtitle={
                    productIdentifier
                      ? 'Consultar por este producto'
                      : 'Iniciar conversación'
                  }
                  color="#25D366"
                  onClick={
                    openWhatsapp
                  }
                />

                <ContactOption
                  icon={
                    <EmailIcon />
                  }
                  title="Enviar consulta"
                  subtitle={
                    productIdentifier
                      ? 'Consultar por este producto por correo'
                      : 'Completar formulario por correo'
                  }
                  color={
                    colorPrimario
                  }
                  onClick={
                    openFormulario
                  }
                />
              </Stack>
            </Paper>
          </Zoom>
        ) : null}

        {/* ============================================================= */}
        {/* Único botón flotante                                          */}
        {/* ============================================================= */}

        <Zoom in>
          <Tooltip
            title={
              mainTooltip
            }
            placement="left"
          >
            <Fab
              aria-label={
                mainAriaLabel
              }
              onClick={
                handleMainClick
              }
              sx={{
                width: {
                  xs: 56,
                  sm: 60,
                },

                height: {
                  xs: 56,
                  sm: 60,
                },

                bgcolor:
                  mainBg,

                color: '#fff',

                boxShadow:
                  '0 8px 28px rgba(0,0,0,0.22)',

                transition:
                  'transform 160ms ease, filter 160ms ease, box-shadow 160ms ease',

                '&:hover': {
                  bgcolor:
                    mainBg,

                  filter:
                    'brightness(0.92)',

                  transform:
                    'translateY(-2px)',

                  boxShadow:
                    '0 12px 32px rgba(0,0,0,0.26)',
                },

                '&:focus-visible': {
                  outline:
                    `3px solid ${mainBg}4d`,
                  outlineOffset: 3,
                },
              }}
            >
              {mainIcon}
            </Fab>
          </Tooltip>
        </Zoom>
      </Box>
    </ClickAwayListener>
  );
}

/* ===================================================================== */
/*  Opción del menú                                                      */
/* ===================================================================== */

function ContactOption({
  icon,
  title,
  subtitle,
  color,
  onClick,
}) {
  return (
    <ButtonBase
      onClick={onClick}
      sx={{
        width: '100%',
        p: 1.15,
        borderRadius: 2,
        textAlign: 'left',
        justifyContent:
          'flex-start',

        '&:hover': {
          bgcolor:
            '#f8fafc',
        },
      }}
    >
      <Stack
        direction="row"
        spacing={1.1}
        alignItems="center"
        sx={{
          width: '100%',
        }}
      >
        <Box
          sx={{
            width: 40,
            height: 40,
            display: 'grid',
            placeItems: 'center',
            borderRadius: 2,
            bgcolor:
              `${color}12`,
            color,
            flexShrink: 0,
          }}
        >
          {icon}
        </Box>

        <Box
          sx={{
            minWidth: 0,
          }}
        >
          <Typography
            variant="body2"
            fontWeight={900}
            sx={{
              color:
                '#0f172a',
            }}
          >
            {title}
          </Typography>

          <Typography
            variant="caption"
            sx={{
              display: 'block',
              mt: 0.1,
              color:
                '#64748b',
              lineHeight: 1.35,
            }}
          >
            {subtitle}
          </Typography>
        </Box>
      </Stack>
    </ButtonBase>
  );
}
